import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { color, r, radius } from '../../theme/tokens';
import GeneratingMark from '../Dashboard/components/GeneratingMark';
import {
  CHECKING_BODY,
  CHECKING_TITLE,
  COULDNT_CHECK_BODY,
  COULDNT_CHECK_TITLE,
  TRY_AGAIN,
} from './copy';

// v5.1's checking screen (state 7) and couldn't-check screen (state 11),
// STACK-587. Both show the lists being checked. Couldn't-check is one message
// for every refusal (Tanu, 2026-10-16) and shares nothing with a result row,
// so it can never read as a clean check.

/** "Warfarin, Sertraline" over "St. John's Wort, Ginkgo biloba, Magnesium". */
export const CheckedLists = ({ medications, supplements }) => (
  <Box
    data-testid="checker-checked-lists"
    sx={{
      backgroundColor: color.paper,
      border: `${r(1)} solid ${color.line}`,
      borderRadius: radius.lg,
      px: r(16),
      py: r(14),
      fontSize: r(14),
      lineHeight: 1.5,
      color: color.textBody,
    }}
  >
    <Box component="b" sx={{ display: 'block', color: color.ink }}>
      {medications.map((m) => m.display_name).join(', ')}
    </Box>
    {supplements.map((s) => s.name).join(', ')}
  </Box>
);

export const CheckingScreen = ({ medications, supplements }) => (
  <>
    <CheckedLists medications={medications} supplements={supplements} />
    <Box
      data-testid="checker-checking"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: r(20),
        py: r(64),
        textAlign: 'center',
      }}
    >
      {/* The synthesis mark's loop (1.6 s, ease-in-out), two-tone as v5.1
          and the brief draw it; still under reduced motion. The copy below
          is the live region. */}
      <GeneratingMark label={null} size={r(72)} markColor={color.evergreen} synthesisColor={color.accent} />
      <Box role="status" aria-live="polite">
        <Typography sx={{ fontSize: r(17), fontWeight: 700, color: color.ink }}>{CHECKING_TITLE}</Typography>
        <Typography sx={{ mt: r(4), fontSize: r(14), color: color.inkSoft }}>{CHECKING_BODY}</Typography>
      </Box>
    </Box>
  </>
);

export const CouldntCheckScreen = ({ medications, supplements, onRetry }) => (
  <>
    <CheckedLists medications={medications} supplements={supplements} />
    <Box
      role="alert"
      data-testid="checker-couldnt-check"
      sx={{
        backgroundColor: color.paper,
        border: `${r(1)} solid ${color.line}`,
        borderRadius: radius.lg,
        px: r(18),
        py: r(20),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: r(8),
      }}
    >
      <Typography component="h2" sx={{ m: 0, fontSize: r(20), fontWeight: 800, letterSpacing: '-0.02em', color: color.ink }}>
        {COULDNT_CHECK_TITLE}
      </Typography>
      <Typography sx={{ m: 0, fontSize: r(15), lineHeight: 1.5, color: color.textBody }}>{COULDNT_CHECK_BODY}</Typography>
      <Button
        variant="outlined"
        onClick={onRetry}
        sx={{
          mt: r(8),
          minHeight: r(44),
          px: r(20),
          borderRadius: radius.md,
          borderColor: color.line,
          color: color.evergreen,
          fontWeight: 700,
          fontSize: r(15),
          textTransform: 'none',
          '&:hover': { borderColor: color.evergreen, backgroundColor: color.paper },
        }}
      >
        {TRY_AGAIN}
      </Button>
    </Box>
  </>
);
